import {
  Anchor,
  Button,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Divider,
  Box,
  Container,
  Title,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { Link, useNavigate } from "react-router-dom";
import { User } from "../common/models";
import { useState, useEffect } from "react";
import BrandLogoName from "../components/BrandLogoName";
import { IconArrowRight } from "@tabler/icons-react";
import { loginUser } from "../api/authentication";
import { ApiService } from "../api/api-service";
import { notifications } from "@mantine/notifications";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../auth/authConfig";
import { isBatchOnlyEmail } from "../auth/batchOnlyAccess";

export default function LoginPage() {
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  const navigate = useNavigate();

  const [user, saveUser] = useLocalStorage<User | undefined>({
    key: "user",
    defaultValue: undefined,
    serialize: JSON.stringify,
    deserialize: (str) => (str ? JSON.parse(str) : undefined),
  });

  const { instance } = useMsal();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  async function login() {
    setLoggingIn(true);
    try {
      const response = await loginUser(username, password);
      if (response instanceof Error) {
        notifications.show({
          title: "Login Failed",
          message: response.message,
          color: "red",
        });
      } else {
        const { token, user } = response;
        localStorage.setItem("token", token as string);
        // Store applicationRole for role-based access
        if (user.applicationRole) {
          localStorage.setItem("applicationRole", JSON.stringify(user.applicationRole));
        }
        saveUser(user);
        navigate(isBatchOnlyEmail(user.email) ? "/Batches" : "/dashboard");
      }
    } catch (error) {
      notifications.show({
        title: "Login Failed",
        message: error instanceof Error ? error.message : "Unknown error",
        color: "red",
      });
    } finally {
      setLoggingIn(false);
    }
  }

  const handleAzureLogin = async () => {
    try {
      const response = await instance.loginPopup(loginRequest);
      const account = response.account;

      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account,
      });

      const accessToken = tokenResponse.accessToken;

      // Goes through the same axios instance (and baseURL) as every other
      // API call, instead of a hardcoded production URL — otherwise local
      // dev SSO logins were silently answered by the deployed backend and
      // never picked up local role/permission changes.
      const backendRes = await ApiService.getInstance().post(
        "/login/azure-login",
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const data = backendRes.data;

      // The backend doesn't always return firstName/lastName for SSO
      // accounts (e.g. first-time Azure sign-in) — fall back to the
      // display name Azure AD gave us so the avatar/name never render
      // blank after SSO login.
      const azureName = account.name || "";
      const [azureFirstName, ...azureRest] = azureName.split(" ");
      const resolvedUser = {
        ...data.user,
        firstName: data.user?.firstName || azureFirstName || "",
        lastName: data.user?.lastName || azureRest.join(" ") || "",
      };

      localStorage.setItem("token", data.token);
      // Store applicationRole for role-based access (Azure login)
      if (data.user?.applicationRole) {
        localStorage.setItem("applicationRole", JSON.stringify(data.user.applicationRole));
      }
      saveUser(resolvedUser);

      notifications.show({
        title: "Login Successful",
        message: `Welcome ${azureName || data.user.name || account.username}`,
        color: "green",
      });

      navigate(isBatchOnlyEmail(resolvedUser.email) ? "/Batches" : "/dashboard");
    } catch (error: any) {
      console.error("Azure AD Login Error:", error);
      notifications.show({
        title: "Login Failed",
        message:
          error?.response?.data?.message ||
          (error instanceof Error ? error.message : "Unable to login with Microsoft"),
        color: "red",
      });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !loggingIn && username && password) {
      login();
    }
  };

  return (
    <Box
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "20px",
        overflow: "hidden",
      }}
    >
      <Container size={380}>
        <Paper
          withBorder
          shadow="xl"
          p={28}
          radius="lg"
          style={{
            backgroundColor: "white",
          }}
          onKeyDown={handleKeyDown}
        >
          <Stack align="center" gap="xs" mb="lg">
            <BrandLogoName />
            {/* <Title order={2} size="h3" fw={600} mt="xs">
              Welcome Back
            </Title> */}
            {/* <Text c="dimmed" size="sm">
              Sign in to your account to continue
            </Text> */}
          </Stack>

          <Stack gap="sm">
            <TextInput
              label="Username"
              placeholder="Enter your username"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              size="sm"
              styles={{
                label: {
                  fontSize: "13px",
                  fontWeight: 500,
                  marginBottom: "6px",
                },
              }}
            />

            <PasswordInput
              label="Password"
              placeholder="Enter your password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              size="sm"
              styles={{
                label: {
                  fontSize: "13px",
                  fontWeight: 500,
                  marginBottom: "6px",
                },
              }}
            />

            <Button
              fullWidth
              mt="sm"
              size="sm"
              onClick={login}
              disabled={username.length === 0 || password.length === 0}
              rightSection={<IconArrowRight size={16} />}
              loading={loggingIn}
              gradient={{ from: "indigo", to: "violet", deg: 45 }}
              variant="gradient"
            >
              Sign In
            </Button>

            {/* <Divider
              label={
                <Text size="xs" c="dimmed" fw={500}>
                  OR CONTINUE WITH
                </Text>
              }
              labelPosition="center"
              my="sm"
            /> */}

            <Button
              fullWidth
              variant="default"
              size="sm"
              onClick={handleAzureLogin}
              styles={{
                root: {
                  border: "1px solid #dee2e6",
                  "&:hover": {
                    backgroundColor: "#f8f9fa",
                  },
                },
              }}
            >
              Continue with SSO
            </Button>
          </Stack>

          <Divider my="md" />

          <Stack gap={4}>
            <Text ta="center" size="xs" c="dimmed">
              © {new Date().getFullYear()} Thinkcloudly
            </Text>
            <Text ta="center" size="xs" c="dimmed">
              <Anchor component={Link} to="/privacy-policy" size="xs" c="dimmed">
                Privacy Policy
              </Anchor>{" "}
              |{" "}
              <Anchor component={Link} to="/terms-of-service" size="xs" c="dimmed">
                Terms of Service
              </Anchor>
            </Text>
            <Text ta="center" size="xs" c="dimmed" mt={2}>
              This Admin Panel is a private productivity tool for managing students and trainers
            </Text>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}